/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect, useMemo, useState } from 'react'

import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'

import { Alert, PageContainer, PageHeader } from '@/components/ui'
import { useApi } from '@/hooks'

import { parseRunTarget, runMigration } from './runMigration'
import { RunSummary } from './RunSummary'
import { STATUS_LABEL, SideBySide } from './SideBySide'
import type { RowStatus, RunResult } from './types'
import { useMigrationFilters } from './useMigrationFilters'

import { LoadingPanel } from '../LoadingPanel'

type View = 'all' | 'discrepancies' | RowStatus

const VIEWS: { value: View; label: string }[] = [
  { value: 'discrepancies', label: 'Discrepancies' },
  { value: 'all', label: 'All rows' },
  ...(Object.keys(STATUS_LABEL) as RowStatus[])
    .filter((s) => s !== 'match')
    .map((s) => ({ value: s as View, label: STATUS_LABEL[s] })),
]

/**
 * Every row of a run side by side. Opens on the result the previous page already has (router
 * state); a direct link or a reload runs it again from the filters in the URL.
 */
const MigrationComparePage: React.FC = () => {
  const { api } = useApi()
  const { id = '' } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const location = useLocation()
  const { filters, search } = useMigrationFilters()
  const target = parseRunTarget(params.get('run'))
  const passed = (location.state as { result?: RunResult } | null)?.result
  const [result, setResult] = useState<RunResult | undefined>(passed)
  const [error, setError] = useState('')
  const [view, setView] = useState<View>('discrepancies')

  const ready = Boolean(filters.companyId && filters.from && filters.to)
  useEffect(() => {
    if (result || !ready) return
    const run = async () => {
      const out = await runMigration(api, id, target, filters)
      setResult(out.result)
      setError(out.error ?? '')
    }
    void run()
  }, [api, id, params, ready, filters, result])

  const rows = useMemo(() => {
    const all = result?.compare?.rows ?? []
    if (view === 'all') return all
    if (view === 'discrepancies') return all.filter((r) => r.status !== 'match')
    return all.filter((r) => r.status === view)
  }, [result, view])

  const backTo = `/admin/migration/${encodeURIComponent(id)}?${new URLSearchParams(
    [...new URLSearchParams(search)].filter(([k]) => k !== 'run')
  ).toString()}`

  return (
    <PageContainer>
      <Link to={backTo} className="link text-sm">
        ← Back
      </Link>
      <PageHeader title="Side by side" subtitle={`${id} — ${target.kind === 'test' ? 'API test' : target.sqlId}`} />
      {error && <Alert message={error} type="error" />}
      {!result && !error && <LoadingPanel label="Running on both back ends…" />}
      {result && (
        <div className="flex flex-col gap-4">
          <RunSummary result={result} />
          <div className="join">
            {VIEWS.map((v) => (
              <button
                key={v.value}
                type="button"
                className={`btn join-item btn-sm ${view === v.value ? 'btn-active' : ''}`}
                onClick={() => setView(v.value)}
              >
                {v.label}
              </button>
            ))}
          </div>
          <p className="text-sm text-base-content/60">{rows.length} rows</p>
          <SideBySide rows={rows} />
        </div>
      )}
    </PageContainer>
  )
}

export default MigrationComparePage
